/**
 * Aceite dos Termos no fim do cadastro.
 *
 * Usado pela tela dedicada `/termos` (braço controle) e pelo aceite embutido
 * na última tela de dado (braço `guided` do signup-ux-ab). Os dois caminhos
 * precisam gravar a mesma prova: POST /registration/complete com a versão
 * do bundle, e honrar `loadingTrap`, `blocked` e `tacRequired`.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { logUxEvent } from '../telemetry/uxLogger';
import { TERMS_OF_USE_VERSION } from '../terms/termsVersion';
import {
  patientFacingTermsAcceptError,
  postRegistrationComplete,
} from '../terms/postRegistrationComplete';
import { setFlowVariant, skipsTriage } from '../utils/flowVariant';
import {
  nextSignupRoute,
  getSignupVariant,
  isTermsFirst,
} from '../utils/signupOrderAb';
import { markSignupHistoryCompleted } from '../components/SignupHistoryGuard';

/**
 * Mesma origem, como o resto do funil (`/registration/start`, `/step`,
 * `/lookup`). Os dois hostings — `app` e `org` — têm rewrite de
 * `/registration/**` para o apiV2, então não há motivo para o aceite ser a
 * única chamada cross-origin: o preflight de CORS custava ~1,7s extra e
 * gerava `fetch_error` quando bloqueado por extensão ou rede corporativa.
 */
export const SIGNUP_TERMS_API_URL =
  process.env.REACT_APP_API_URL ||
  (typeof window !== 'undefined' ? window.location.origin : '');

export const SIGNUP_TERMS_BLOCKED_DEFAULT =
  'Identificamos, em consulta(s) anterior(es) vinculada(s) a este cadastro, o descumprimento de disposições dos nossos Termos de Uso. Por esse motivo, o acesso a novas consultas na plataforma foi bloqueado.';

export function useSignupTermsAccept() {
  const navigate = useNavigate();
  const [showLevelUp, setShowLevelUp] = useState(false);
  const [blockedMessage, setBlockedMessage] = useState('');
  const [acceptError, setAcceptError] = useState('');
  const [tacInfo, setTacInfo] = useState(null);
  const [loadingTrap, setLoadingTrap] = useState(false);

  const acceptTerms = async (extraLog = {}) => {
    setAcceptError('');
    logUxEvent('terms_accept', extraLog);

    const patientId =
      sessionStorage.getItem('patientId') || localStorage.getItem('patientId') || '';
    if (patientId) {
      const url = SIGNUP_TERMS_API_URL.replace(/\/+$/, '');
      try {
        const { data } = await postRegistrationComplete({
          apiBaseUrl: url,
          patientId,
          termsOfUseVersion: TERMS_OF_USE_VERSION,
        });
        if (data && data.loadingTrap === true) {
          setLoadingTrap(true);
          return false;
        }
        if (data && data.blocked === true) {
          setBlockedMessage(data.message || SIGNUP_TERMS_BLOCKED_DEFAULT);
          return false;
        }
        if (data && data.tacRequired === true) {
          setTacInfo(data);
          return false;
        }
        if (!data || data.success !== true) {
          setAcceptError(patientFacingTermsAcceptError(data));
          return false;
        }
        if (data.flowVariant) setFlowVariant(data.flowVariant, patientId);
      } catch {
        setAcceptError('Falha de conexão. Verifique sua internet e tente novamente.');
        return false;
      }
    }

    setShowLevelUp(true);
    return true;
  };

  const goAfterAccept = () => {
    const signupVariant = getSignupVariant();
    const termsFirst = isTermsFirst(signupVariant);
    const nextInBranch = termsFirst ? nextSignupRoute('termos', signupVariant) : null;
    const straightToChat = skipsTriage();
    if (!nextInBranch) markSignupHistoryCompleted();
    navigate(nextInBranch || (straightToChat ? '/consulta/chat' : '/triagem'));
  };

  const levelUpCopy = () => {
    const termsFirst = isTermsFirst(getSignupVariant());
    const straightToChat = skipsTriage();
    return {
      title: termsFirst ? 'Vamos começar' : 'Cadastro concluído',
      subtitle: termsFirst
        ? 'Agora vamos preencher os seus dados de cadastro'
        : straightToChat
          ? 'A seguir, você entra direto no chat do atendimento'
          : 'A seguir, iniciaremos a triagem com a assistente Alice',
    };
  };

  return {
    acceptTerms,
    showLevelUp,
    blockedMessage,
    setBlockedMessage,
    acceptError,
    tacInfo,
    setTacInfo,
    loadingTrap,
    goAfterAccept,
    levelUpCopy,
    patientId:
      (typeof window !== 'undefined' &&
        (sessionStorage.getItem('patientId') || localStorage.getItem('patientId'))) ||
      '',
  };
}
